import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

interface EnrichPayload {
  apollo: { title: string | null; phone: string | null; linkedinUrl: string | null } | null;
  firm: {
    firmBio: string | null;
    practiceAreas: string[];
    offices: string[];
    partnerCount: number | null;
    firmWebsite: string | null;
    scrapedAt: string;
    source: string;
  } | null;
  errors: { apollo?: string; firm?: string };
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  payload: EnrichPayload | null;
  onApply: (fields: Record<string, unknown>) => void;
  applying?: boolean;
}

export function CoiEnrichReviewDialog({ open, onOpenChange, payload, onApply, applying }: Props) {
  const [picked, setPicked] = useState<Record<string, boolean>>({});

  const toggle = (key: string) => setPicked((p) => ({ ...p, [key]: !p[key] }));

  const apply = () => {
    if (!payload) return;
    const fields: Record<string, unknown> = {};
    if (payload.apollo) {
      for (const k of ["title", "phone", "linkedinUrl"] as const) {
        if (picked[`apollo.${k}`] && payload.apollo[k]) fields[k] = payload.apollo[k];
      }
    }
    if (payload.firm) {
      if (picked["firm.bio"] && payload.firm.firmBio) fields.firmBio = payload.firm.firmBio;
      if (picked["firm.meta"]) {
        fields.firmMetadata = {
          practiceAreas: payload.firm.practiceAreas,
          offices: payload.firm.offices,
          partnerCount: payload.firm.partnerCount,
          firmWebsite: payload.firm.firmWebsite,
          scrapedAt: payload.firm.scrapedAt,
          source: payload.firm.source,
        };
      }
    }
    onApply(fields);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Review Enrichment</DialogTitle></DialogHeader>
        {!payload ? (
          <p className="text-sm text-muted-foreground">No data.</p>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <Section title="Apollo (person)">
              {payload.apollo ? (
                <div className="space-y-1.5">
                  {(["title", "phone", "linkedinUrl"] as const).map((k) =>
                    payload.apollo![k] ? (
                      <Row key={k} keyId={`apollo.${k}`} label={k} value={String(payload.apollo![k])} picked={picked} toggle={toggle} />
                    ) : null
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{payload.errors.apollo ?? "No Apollo data returned."}</p>
              )}
            </Section>
            <Section title="Firm (website)">
              {payload.firm ? (
                <div className="space-y-1.5">
                  {payload.firm.firmBio && <Row keyId="firm.bio" label="Firm Bio" value={payload.firm.firmBio} picked={picked} toggle={toggle} />}
                  <Row keyId="firm.meta" label="Practice areas / offices / partners" picked={picked} toggle={toggle}
                    value={`${payload.firm.practiceAreas.join(", ") || "—"} · ${payload.firm.offices.join(", ") || "—"} · ${payload.firm.partnerCount ?? "—"} partners`} />
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{payload.errors.firm ?? "Couldn't reach the firm website."}</p>
              )}
            </Section>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={apply} disabled={applying}>{applying ? "Applying…" : "Apply Selected"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{title}</h4>
      {children}
    </div>
  );
}

function Row({ keyId, label, value, picked, toggle }: { keyId: string; label: string; value: string; picked: Record<string, boolean>; toggle: (k: string) => void }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer rounded p-2 hover:bg-muted/30">
      <Checkbox checked={!!picked[keyId]} onCheckedChange={() => toggle(keyId)} />
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm break-words">{value}</div>
      </div>
    </label>
  );
}
